import { useState, useEffect, useCallback } from 'react';
import apiClient from '@/services/apiClient';
import * as printerApi from '@/services/printerApi';
import * as networkPrintApi from '@/services/networkPrintApi';

// Reusable USN-sticker printing logic (extracted from Folding Print so the same
// setup + Print Bridge routing can be used inside Segregation). Defaults to the
// existing 'folding-print' operation + settings so operators need zero re-setup.
const DEFAULT_SETTINGS = {
  configId: '',
  printerLanguage: 'tspl',
  copies: 1,
  printerVendorId: null,
  printerProductId: null,
  printerName: '',
};

const loadSettings = (storageKey) => {
  try {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return { ...DEFAULT_SETTINGS };
    return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch {
    return { ...DEFAULT_SETTINGS };
  }
};

const getAuthorizedDevice = async (vendorId, productId) => {
  if (!('usb' in navigator)) return null;
  try {
    const devices = await navigator.usb.getDevices();
    return devices.find((d) => d.vendorId === vendorId && d.productId === productId) || null;
  } catch {
    return null;
  }
};

export const useUsnStickerPrint = ({
  screenKey = 'folding-print',
  storageKey = 'foldingPrint.settings.v1',
} = {}) => {
  const [settings, setSettings] = useState(() => loadSettings(storageKey));
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [labelConfigs, setLabelConfigs] = useState([]);
  const [loadingConfigs, setLoadingConfigs] = useState(false);
  const [connectingPrinter, setConnectingPrinter] = useState(false);
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printMethod, setPrintMethod] = useState(
    () => localStorage.getItem(`lp_method::${screenKey}`) || 'usb'
  );
  const [opRoute, setOpRoute] = useState(null);

  const networkActive = printMethod === 'network';
  const usbSupported = 'usb' in navigator;

  const persist = (next) => {
    try { localStorage.setItem(storageKey, JSON.stringify(next)); } catch { /* ignore */ }
  };

  const updateSetting = (key, value) => {
    setSettings((prev) => {
      const next = { ...prev, [key]: value };
      persist(next);
      return next;
    });
  };

  const loadLabelConfigs = useCallback(async () => {
    setLoadingConfigs(true);
    try {
      const res = await apiClient.get('/label-configs/list');
      const list = res.data.configs || [];
      setLabelConfigs(list);
      setSettings((prev) => {
        if (prev.configId) return prev;
        const def = list.find((c) => (c.section || '').toLowerCase() === 'inhouse')
          || list.find((c) => c.is_default) || list[0];
        if (!def) return prev;
        const next = { ...prev, configId: def.config_id };
        persist(next);
        return next;
      });
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingConfigs(false);
    }
  }, []);

  useEffect(() => { loadLabelConfigs(); }, [loadLabelConfigs]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await networkPrintApi.getOperationRoute(screenKey);
        if (!cancelled) setOpRoute(res);
      } catch (_) { /* optional */ }
    })();
    return () => { cancelled = true; };
  }, [screenKey]);

  useEffect(() => {
    (async () => {
      if (!settings.printerVendorId || !settings.printerProductId) {
        setPrinterConnected(false); return;
      }
      const dev = await getAuthorizedDevice(settings.printerVendorId, settings.printerProductId);
      setPrinterConnected(!!dev);
    })();
  }, [settings.printerVendorId, settings.printerProductId]);

  const connectPrinter = async () => {
    if (!usbSupported) {
      throw new Error('Web USB is not supported in this browser. Use Chrome/Edge on desktop.');
    }
    setConnectingPrinter(true);
    try {
      const device = await printerApi.requestUSBPrinter();
      const next = {
        ...settings,
        printerVendorId: device.vendorId,
        printerProductId: device.productId,
        printerName: device.productName
          || `VID:${device.vendorId.toString(16)} PID:${device.productId.toString(16)}`,
      };
      setSettings(next);
      persist(next);
      setPrinterConnected(true);
      return next.printerName;
    } finally {
      setConnectingPrinter(false);
    }
  };

  const forgetPrinter = () => {
    const next = { ...settings, printerVendorId: null, printerProductId: null, printerName: '' };
    setSettings(next);
    persist(next);
    setPrinterConnected(false);
  };

  const ready = !!settings.configId
    && (networkActive || (!!settings.printerVendorId && printerConnected));

  // Print a set of USN stickers. Throws on failure. Returns number printed.
  const printUsns = async (usns, { sortNo = '', sortNos: sortNosArg = null, bundleCode = '', markPrinted = false } = {}) => {
    const list = (usns || []).filter(Boolean);
    if (!list.length) throw new Error('No USN stickers to print');
    if (!settings.configId) {
      setSettingsOpen(true);
      throw new Error('Open Printer Setup and select a label configuration first');
    }
    const useNetwork = printerApi.isNetworkPrintActive();
    if (!useNetwork && (!settings.printerVendorId || !settings.printerProductId)) {
      setSettingsOpen(true);
      throw new Error('Connect a USB printer in Setup, or switch to Network printing.');
    }
    let device;
    if (useNetwork) {
      device = await printerApi.requestUSBPrinter(); // network stub
    } else {
      device = await getAuthorizedDevice(settings.printerVendorId, settings.printerProductId);
      if (!device) {
        setPrinterConnected(false);
        setSettingsOpen(true);
        throw new Error('Printer not found. Reconnect in Setup.');
      }
    }
    let sortNos = null;
    if (Array.isArray(sortNosArg) && sortNosArg.length === list.length) {
      sortNos = sortNosArg.map((s) => (s ? `Sort: ${s}` : ''));
    } else if (sortNo) {
      sortNos = list.map(() => `Sort: ${sortNo}`);
    }
    const cmdResp = await printerApi.generateUSNLabels(
      list, settings.printerLanguage, parseInt(settings.copies, 10) || 1, sortNos, settings.configId
    );
    await printerApi.sendToUSBPrinter(device, cmdResp.commands);
    if (markPrinted && bundleCode) {
      try { await apiClient.post(`/folding/mark-printed/${encodeURIComponent(bundleCode)}`); } catch { /* non-blocking */ }
    }
    return list.length;
  };

  const printDestination = networkActive
    ? (opRoute?.route?.agent_name || 'network station')
    : (settings.printerName || 'printer');

  return {
    settings, updateSetting,
    settingsOpen, setSettingsOpen,
    labelConfigs, loadingConfigs,
    printerConnected, usbSupported,
    connectingPrinter, connectPrinter, forgetPrinter,
    printMethod, setPrintMethod, networkActive, opRoute,
    ready, printUsns, printDestination,
  };
};

export default useUsnStickerPrint;
